<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { useAlert } from 'dashboard/composables';

const DESKTOP_WIDTH = 1280;
const DESKTOP_HEIGHT = 800;
const DESKTOP_SCALE = 0.56;
const DESKTOP_STAGE_WIDTH = Math.round(DESKTOP_WIDTH * DESKTOP_SCALE);
const DESKTOP_STAGE_HEIGHT = Math.round(DESKTOP_HEIGHT * DESKTOP_SCALE);

const props = defineProps({
  websiteToken: { type: String, default: '' },
  widgetUrl: { type: String, default: '' },
  bookingFlow: { type: Object, default: () => ({}) },
  widgetColor: { type: String, default: '#7C5CFC' },
  backgroundColor: { type: String, default: '#ffffff' },
  designOptions: { type: Object, default: null },
  selectedPageId: { type: String, default: null },
  /** Fill parent instead of fixed aside widths (Roo side panel). */
  embedded: { type: Boolean, default: false },
  /** Hide chrome (URL/copy/device toggle) when embedded. */
  hideChrome: { type: Boolean, default: false },
});

const emit = defineEmits(['select-page']);

const { t } = useI18n();
const mode = ref('mobile');
const iframeRef = ref(null);
const copied = ref(false);

const publicWidgetUrl = computed(() => {
  const url = (props.widgetUrl || '').trim();
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  const origin =
    typeof window !== 'undefined' ? window.location.origin : '';
  return `${origin}${url.startsWith('/') ? url : `/${url}`}`;
});

const openWidgetUrl = () => {
  if (!publicWidgetUrl.value) return;
  window.open(publicWidgetUrl.value, '_blank', 'noopener,noreferrer');
};

const copyWidgetUrl = async () => {
  if (!publicWidgetUrl.value) return;
  await copyTextToClipboard(publicWidgetUrl.value);
  copied.value = true;
  useAlert(t('BOOKING_WIDGETS.COPY_URL_SUCCESS'));
  window.setTimeout(() => {
    copied.value = false;
  }, 1500);
};

const previewUrl = computed(() => {
  if (!props.websiteToken) return '';
  const params = new URLSearchParams({
    preview: '1',
    device: mode.value,
    widget_color: props.widgetColor || '#7C5CFC',
    background_color: props.backgroundColor || '#ffffff',
  });
  return `/booking_widget/${encodeURIComponent(props.websiteToken)}?${params}`;
});

const cloneable = value => {
  if (value == null) return value;
  try {
    return JSON.parse(JSON.stringify(value));
  } catch {
    return null;
  }
};

const postToPreview = payload => {
  const frame = iframeRef.value;
  if (!frame?.contentWindow) return;
  frame.contentWindow.postMessage(cloneable(payload), window.location.origin);
};

const postDesign = () => {
  const payload = {
    event: 'rookooBookingWidgetSetDesign',
    widgetColor: props.widgetColor,
    backgroundColor: props.backgroundColor,
  };
  if (props.designOptions) {
    payload.designOptions = props.designOptions;
  }
  postToPreview(payload);
};

const syncPreview = () => {
  postToPreview({
    event: 'rookooBookingWidgetSetFlow',
    bookingFlow: props.bookingFlow || {},
  });
  postDesign();
  if (props.selectedPageId) {
    postToPreview({
      event: 'rookooBookingWidgetSetPage',
      pageId: props.selectedPageId,
    });
  }
};

watch(
  () => [props.bookingFlow, props.selectedPageId],
  () => syncPreview(),
  { deep: true }
);

watch(
  () => [props.widgetColor, props.backgroundColor, props.designOptions],
  () => postDesign(),
  { deep: true }
);

const onLoad = () => {
  syncPreview();
};

const onPreviewMessage = event => {
  if (event.origin !== window.location.origin) return;
  const data = event.data || {};
  if (data.event !== 'rookooBookingWidgetPageChanged') return;
  const pageId = data.pageId != null ? String(data.pageId) : '';
  if (!pageId || pageId === String(props.selectedPageId || '')) return;
  emit('select-page', pageId);
};

onMounted(() => {
  window.addEventListener('message', onPreviewMessage);
});

onUnmounted(() => {
  window.removeEventListener('message', onPreviewMessage);
});
</script>

<template>
  <aside
    class="flex h-full min-h-0 shrink-0 flex-col overflow-hidden bg-slate-950 transition-[width] duration-200"
    :class="[
      embedded ? 'w-full border-0' : 'border-l border-n-weak',
      embedded ? '' : mode === 'desktop' ? 'w-[780px]' : 'w-[480px]',
    ]"
  >
    <div
      v-if="!hideChrome"
      class="flex h-14 shrink-0 items-center gap-2 border-b border-slate-800 px-6"
    >
      <NextButton
        sm
        faded
        slate
        type="button"
        icon="i-lucide-square-arrow-out-up-right"
        class="shrink-0 !text-slate-300 hover:!bg-slate-800 hover:!text-white px-3"
        :aria-label="t('BOOKING_WIDGETS.PREVIEW')"
        @click="openWidgetUrl"
      >
        {{ t('BOOKING_WIDGETS.PREVIEW') }}
      </NextButton>

      <div
        v-if="publicWidgetUrl"
        class="flex h-7 min-w-0 flex-1 items-center gap-1"
      >
        <NextButton
          sm
          faded
          slate
          type="button"
          :icon="copied ? 'i-lucide-check' : 'i-lucide-copy'"
          class="shrink-0 !text-slate-300 hover:!bg-slate-800 hover:!text-white"
          :aria-label="t('BOOKING_WIDGETS.COPY_URL')"
          @click="copyWidgetUrl"
        />
      </div>

      <div class="flex shrink-0 gap-1 rounded-lg bg-n-slate-9/10 p-1">
        <button
          type="button"
          class="rounded-md px-3 py-1 text-sm"
          :class="
            mode === 'desktop'
              ? 'bg-slate-700 text-white'
              : 'text-slate-300'
          "
          @click="mode = 'desktop'"
        >
          {{ t('BOOKING_WIDGETS.FULL_SCREEN') }}
        </button>
        <button
          type="button"
          class="rounded-md px-3 py-1 text-sm"
          :class="
            mode === 'mobile' ? 'bg-slate-700 text-white' : 'text-slate-400'
          "
          @click="mode = 'mobile'"
        >
          {{ t('BOOKING_WIDGETS.MOBILE') }}
        </button>
      </div>
    </div>

    <div
      class="flex min-h-0 flex-1 justify-center overflow-auto p-7"
      :class="mode === 'desktop' ? 'items-start' : 'items-stretch'"
    >
      <div
        v-if="previewUrl && mode === 'desktop'"
        class="relative shrink-0 overflow-hidden rounded-2xl bg-slate-950 shadow-2xl"
        :style="{
          width: `${DESKTOP_STAGE_WIDTH}px`,
          height: `${DESKTOP_STAGE_HEIGHT}px`,
        }"
      >
        <iframe
          :key="previewUrl"
          ref="iframeRef"
          :src="previewUrl"
          class="absolute left-0 top-0 origin-top-left border-0 bg-transparent"
          :style="{
            width: `${DESKTOP_WIDTH}px`,
            height: `${DESKTOP_HEIGHT}px`,
            transform: `scale(${DESKTOP_SCALE})`,
          }"
          title="Booking widget preview"
          @load="onLoad"
        />
      </div>
      <iframe
        v-else-if="previewUrl"
        :key="previewUrl"
        ref="iframeRef"
        :src="previewUrl"
        class="min-h-0 min-w-0 w-full flex-1 rounded-2xl border-0 bg-transparent shadow-2xl"
        title="Booking widget preview"
        @load="onLoad"
      />
    </div>
  </aside>
</template>
